/**
 * Kanban Board Component
 * Main board with drag-and-drop functionality
 */

import React, { useState, useEffect } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import { TicketListItem, TicketStatus } from '@/types';
import { KanbanColumn } from './KanbanColumn';
import { KanbanCard } from './KanbanCard';
import { useUpdateTicket } from '@/hooks';
import { toast } from 'sonner';

interface KanbanBoardProps {
  tickets: TicketListItem[];
  onAddTicket?: (status: TicketStatus) => void;
  compact?: boolean;
}

const KANBAN_STATUSES: TicketStatus[] = [
  TicketStatus.NEW,
  TicketStatus.TRIAGED,
  TicketStatus.IN_PROGRESS,
  TicketStatus.IN_REVIEW,
  TicketStatus.WAITING_CUSTOMER,
  TicketStatus.RESOLVED,
  TicketStatus.CLOSED,
  TicketStatus.CANCELLED,
];

export function KanbanBoard({ tickets, onAddTicket, compact = false }: KanbanBoardProps) {
  const [activeTicket, setActiveTicket] = useState<TicketListItem | null>(null);

  // Filter out archived tickets — they should not appear on the board
  const activeTickets = tickets.filter((t) => t.status !== TicketStatus.ARCHIVED);
  const [localTickets, setLocalTickets] = useState<TicketListItem[]>(activeTickets);
  const updateTicketMutation = useUpdateTicket();

  useEffect(() => {
    setLocalTickets(tickets.filter((t) => t.status !== TicketStatus.ARCHIVED));
  }, [tickets]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Group tickets by status
  const ticketsByStatus = KANBAN_STATUSES.reduce((acc, status) => {
    acc[status] = localTickets.filter((ticket) => ticket.status === status);
    return acc;
  }, {} as Record<TicketStatus, TicketListItem[]>);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const ticket = localTickets.find((t) => t.id === active.id);
    if (ticket) {
      setActiveTicket(ticket);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTicket(null);

    if (!over || active.id === over.id) return;

    const ticketId = active.id as string;
    const newStatus = over.id as TicketStatus;

    const ticket = localTickets.find((t) => t.id === ticketId);

    if (!ticket) return;

    if (ticket.status === newStatus) return;

    // Optimistic UI update
    setLocalTickets((prev) =>
      prev.map((t) =>
        t.id === ticketId
          ? {
              ...t,
              status: newStatus,
            }
          : t
      )
    );

    // API update
    updateTicketMutation.mutate(
      {
        ticketId,
        data: { status: newStatus },
      },
      {
        onSuccess: () => {
          toast.success(`Ticket moved to ${newStatus}`);
        },

        onError: (error) => {
          // rollback on error
          setLocalTickets((prev) =>
            prev.map((t) =>
              t.id === ticketId
                ? {
                    ...t,
                    status: ticket.status,
                  }
                : t
            )
          );

          toast.error('Failed to update ticket status');
          console.error(error);
        },
      }
    );
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 overflow-x-auto pb-4 h-full">
        {KANBAN_STATUSES.map((status) => (
          <KanbanColumn
            key={status}
            status={status}
            tickets={ticketsByStatus[status]}
            onAddTicket={onAddTicket}
            compact={compact}
          />
        ))}
      </div>

      <DragOverlay>
        {activeTicket ? (
          <div className="rotate-3 scale-105">
            <KanbanCard ticket={activeTicket} compact={compact} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
